<script setup>
import Boxes from '@/mini/components/Boxes.vue'
import Box from '@/mini/components/Box.vue'
import Button from '@/mini/components/Button.vue'
import Modal from '@/mini/components/Modal.vue'
import CardFormFields from '@/components/CardFormFields.vue'
import Sep from '@/mini/components/Sep.vue'
import { ref, reactive, watch } from 'vue'
import { useMessage } from '@/mini/composables/useMessage'
import { allowedSetTypes, normalizeSetType, buildFullSetId } from '@/utils/cardFormUtils'
import { resolveBlueprintForCard } from '@/composables/useCardtrader'
import { useCardForm } from '@/composables/useCardForm'
import { useAuthStore } from '@/mini/stores/auth'
import { BACKEND_ORIGIN } from '@/config/api'

const props = defineProps({
  visible: { type: Boolean, default: false },
  prefill: { type: Object, default: null },
  availableGroups: { type: Array, default: () => [] },
})

const emit = defineEmits(['close', 'saved'])

const isCreating = ref(false)
const isFetchingBlueprint = ref(false)
const isLoadingCtPrice = ref(false)
const error = ref('')
const cardtraderBlueprint = ref(null)
const cardtraderExpansion = ref(null)
const cardtraderMarketPrice = ref(null)
const lowestCtPrice = ref(null)

async function fetchLowestCtPrice() {
  if (!cardtraderBlueprint.value || isLoadingCtPrice.value) return
  isLoadingCtPrice.value = true
  lowestCtPrice.value = null
  try {
    const authStore = useAuthStore()
    const res = await authStore.authFetch(`${BACKEND_ORIGIN}/api/v1/ct/marketplace/?blueprint_id=${cardtraderBlueprint.value}`)
    if (!res.ok) throw new Error(`${res.status}`)
    const data = await res.json()
    const products = Object.values(data).flat()
    const filtered = products.filter(p =>
      p.properties_hash?.condition === 'Near Mint' &&
      p.properties_hash?.onepiece_language === 'jp' &&
      p.user?.can_sell_sealed_with_ct_zero === true
    )
    if (filtered.length) {
      lowestCtPrice.value = Math.min(...filtered.map(p => p.price_cents)) / 100
      cardtraderMarketPrice.value = lowestCtPrice.value
      card.cardtrader_market_price = lowestCtPrice.value
      if (card.cardtrader_lowest_market_price == null || lowestCtPrice.value < card.cardtrader_lowest_market_price) {
        card.cardtrader_lowest_market_price = lowestCtPrice.value
      }
    } else {
      lowestCtPrice.value = -1
    }
  } catch {
    lowestCtPrice.value = null
  } finally {
    isLoadingCtPrice.value = false
  }
}

const { showSuccessMessage, showDangerMessage } = useMessage()

const card = reactive({
  card_id: '',
  card_name: '',
  set_type: '',
  set_id: '',
  card_number: '',
  card_color: '',
  card_type: '',
  rarity: '',
  foil: 'FALSE',
  fruit: '',
  inventory_price: '',
  market_price: '',
  real_price: '',
  purchase_price: '',
  card_image: '',
  cardtrader_market_price: null,
  cardtrader_lowest_market_price: null,
  to_buy: false,
  favourite: false,
  to_follow: false,
  sell_to_sell: false,
  sell_quantity_to_sell: 1,
  sell_folder: '',
  sell_page: '',
  group_entries: [{ id: null, group_name: '', quantity: 1, folder: '', page: '', warehouse: false }],
})

const {
  authenticatedApiFetch,
  composedCardId,
  isFetchingData,
  fetchCandidates,
  selectedFetchIndex,
  fetchCardData,
  importSelected,
} = useCardForm(card)

async function fetchBlueprint() {
  if (isFetchingBlueprint.value) return
  const cardId = composedCardId.value
  if (!cardId || !card.set_id) {
    showDangerMessage('Set type and card number are required to fetch CardTrader info.')
    return
  }
  isFetchingBlueprint.value = true
  const existingBlueprint = cardtraderBlueprint.value
  if (!existingBlueprint) {
    cardtraderBlueprint.value = null
    cardtraderExpansion.value = null
  }
  try {
    const setType = normalizeSetType(card.set_type, cardId)
    const fullSetId = buildFullSetId(setType, card.set_id)
    const { blueprintId, expansionId } = await resolveBlueprintForCard(fullSetId, cardId)
    if (!existingBlueprint) cardtraderBlueprint.value = blueprintId
    if (expansionId) cardtraderExpansion.value = expansionId
    if (blueprintId || existingBlueprint) showSuccessMessage(`Blueprint #${existingBlueprint ?? blueprintId} — Expansion #${expansionId} found.`)
    else showDangerMessage('No blueprint found for this card on CardTrader.')
  } catch (err) {
    showDangerMessage(err instanceof Error ? err.message : 'Failed to fetch CardTrader info.')
  } finally {
    isFetchingBlueprint.value = false
  }
}

watch(() => props.visible, (val) => {
  if (val && props.prefill) {
    reset()
    if (props.prefill.set_type) card.set_type = props.prefill.set_type
    if (props.prefill.set_id) card.set_id = props.prefill.set_id
    if (props.prefill.card_number) card.card_number = props.prefill.card_number
    if (props.prefill.to_buy != null) card.to_buy = props.prefill.to_buy
    if (card.to_buy && card.group_entries[0]) card.group_entries[0].quantity = 0
    if (props.prefill.group_name && card.group_entries[0]) {
      card.group_entries[0].group_name = props.prefill.group_name
      const group = props.availableGroups.find(g => g.name === props.prefill.group_name)
      if (group) {
        if (group.default_storage_folder) card.group_entries[0].folder = group.default_storage_folder
        if (group.default_storage_page) card.group_entries[0].page = group.default_storage_page
      }
    }
    if (props.prefill.cardtrader_blueprint) cardtraderBlueprint.value = props.prefill.cardtrader_blueprint
    if (props.prefill.cardtrader_expansion) cardtraderExpansion.value = props.prefill.cardtrader_expansion
  }
})

function reset() {
  card.card_id = ''
  card.card_name = ''
  card.set_type = ''
  card.set_id = ''
  card.card_number = ''
  card.card_color = ''
  card.card_type = ''
  card.rarity = ''
  card.foil = 'FALSE'
  card.fruit = ''
  card.inventory_price = ''
  card.market_price = ''
  card.real_price = ''
  card.purchase_price = ''
  card.card_image = ''
  card.cardtrader_market_price = null
  card.cardtrader_lowest_market_price = null
  card.to_buy = false
  card.favourite = false
  card.to_follow = false
  card.sell_to_sell = false
  card.sell_quantity_to_sell = 1
  card.sell_folder = ''
  card.sell_page = ''
  card.group_entries = [{ id: null, group_name: '', quantity: 1, folder: '', page: '', warehouse: false }]
  error.value = ''
  fetchCandidates.value = []
  selectedFetchIndex.value = 0
  cardtraderBlueprint.value = null
  cardtraderExpansion.value = null
  cardtraderMarketPrice.value = null
  lowestCtPrice.value = null
}

function close() {
  reset()
  emit('close')
}

async function save() {
  error.value = ''
  const cardId = composedCardId.value
  if (!cardId || !card.card_name.trim()) {
    error.value = 'Set type and Card number and card name are required.'
    return
  }
  const setType = normalizeSetType(card.set_type, cardId)
  if (!allowedSetTypes.includes(setType)) {
    error.value = 'Set type is required and must be one of: ST, P, OP, EB, PRB.'
    return
  }
  isCreating.value = true
  try {
    const firstEntry = card.group_entries[0] ?? {}
    const payload = {
      card_id: cardId,
      card_name: card.card_name.trim(),
      set_type: setType,
      set_id: buildFullSetId(setType, card.set_id),
      card_number: card.card_number.trim(),
      quantity: Number(firstEntry.quantity) || 0,
      card_color: card.card_color.trim(),
      card_type: card.card_type.trim(),
      rarity: card.rarity.trim(),
      foil: card.foil === 'TRUE',
      cardtrader_warehouse: firstEntry.warehouse ?? false,
      fruit: card.fruit.trim(),
      inventory_price: card.inventory_price === '' ? null : Number(card.inventory_price),
      market_price: card.market_price === '' ? null : Number(card.market_price),
      real_price: card.real_price === '' ? null : Number(card.real_price),
      purchase_price: card.purchase_price === '' ? null : Number(card.purchase_price),
      card_image: card.card_image.trim(),
      cardtrader_blueprint: cardtraderBlueprint.value || null,
      cardtrader_expansion: cardtraderExpansion.value || null,
      cardtrader_market_price: (cardtraderMarketPrice.value && cardtraderMarketPrice.value > 0) ? cardtraderMarketPrice.value : null,
      cardtrader_market_price_fetched_at: (cardtraderMarketPrice.value && cardtraderMarketPrice.value > 0) ? new Date().toISOString() : null,
      set_group: String(firstEntry.group_name || '').trim(),
      to_buy: card.to_buy,
      favourite: card.favourite,
      to_follow: card.to_follow,
      storage: {
        folder: String(firstEntry.folder || '').trim(),
        page: String(firstEntry.page || '').trim(),
      },
      sell: {
        to_sell: card.sell_to_sell,
        quantity_to_sell: Number(card.sell_quantity_to_sell) || 1,
        storage: {
          folder: String(card.sell_folder || '').trim() || null,
          page: String(card.sell_page || '').trim() || null,
        },
      },
    }
    const response = await authenticatedApiFetch('/api/v1/cards/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    if (!response.ok) {
      let message = 'Unable to create card'
      try {
        const contentType = response.headers.get('content-type') || ''
        if (contentType.includes('application/json')) {
          const errorBody = await response.json()
          message = errorBody?.message || errorBody?.error || errorBody?.detail || message
        } else {
          const text = await response.text()
          if (text) message = text
        }
      } catch {}
      throw new Error(message)
    }
    const created = await response.json()

    // Create additional group entries (beyond the first)
    for (const entry of card.group_entries.slice(1)) {
      if (!entry.group_name.trim() && !String(entry.folder || '').trim() && !String(entry.page || '').trim() && (Number(entry.quantity) || 0) === 0) continue
      await authenticatedApiFetch(`/api/v1/cards/${created.id}/groups/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          group_name: entry.group_name.trim() || null,
          quantity: Number(entry.quantity) || 0,
          storage_folder: String(entry.folder || '').trim(),
          storage_page: String(entry.page || '').trim(),
          warehouse: entry.warehouse ?? false,
        }),
      })
    }

    emit('saved')
    close()
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Unable to create card'
  } finally {
    isCreating.value = false
  }
}

</script>

<template>
  <Modal :visible="visible" @close="close">
    <Boxes>
      <Box :size="100">
        <h2 class="m-0">Insert new card</h2>
      </Box>
      <Box :size="100">
        <form class="create-card-form" @submit.prevent="save">
          <Boxes gap="0">
            <CardFormFields
              :form="card"
              :composedCardId="composedCardId"
              :isFetchingData="isFetchingData"
              :fetchCandidates="fetchCandidates"
              :selectedFetchIndex="selectedFetchIndex"
              :availableGroups="props.availableGroups"
              idPrefix="create-"
              mode="create"
              @fetch="fetchCardData"
              @import="importSelected"
              @update:selectedFetchIndex="val => (selectedFetchIndex = val)"
            />
            <Sep class="mb-15"/>
            <Box size="100" class="blue-border b-rad-10 border-1 blue-border p-2 mt-1">
              <Boxes>
                <Box size="100" class="p-0">
                  <h3 class="blue-text h5 m-0">CardTrader</h3>
                </Box>
                <Box size="33" class="p-0">
                  <p class="flex align-items-center">
                    <span class="label">Expansion:</span>&nbsp;
                    <input v-model.number="cardtraderExpansion" type="number" step="1" min="0" class="S p-05 blue-text" style="width: 110px;" placeholder="-" />
                  </p>
                </Box>
                <Box size="33" class="p-0">
                  <p class="flex align-items-center">
                    <span class="label">Blueprint:</span>&nbsp;
                    <input v-model.number="cardtraderBlueprint" type="number" step="1" min="0" class="S p-05 blue-text" style="width: 110px;" placeholder="-" />
                  </p>
                </Box>
                <Box size="33" class="p-0">
                  <p v-if="cardtraderBlueprint && lowestCtPrice !== null" class="m-0">
                    <span v-if="lowestCtPrice === -1" class="label">No NM/JP listings found</span>
                  </p>
                </Box>
                <Box v-if="!cardtraderBlueprint" size="100" class="pb-0 px-0">
                  <p class="m-0">
                    <Button color="blue" invert class="px-15 round" size="S" :disabled="isFetchingBlueprint" @click="fetchBlueprint">
                      {{ isFetchingBlueprint ? 'Fetching...' : 'Fetch CardTrader' }}
                    </Button>
                  </p>
                </Box>
                <Box v-if="cardtraderBlueprint" size="100" class="pb-0 px-0">
                  <p class="m-0">
                    <Button size="S" color="blue" class="px-15 round" :link="`https://www.cardtrader.com/en/cards/${cardtraderBlueprint}`" target="_blank">
                      View on CT ↗
                    </Button>
                    <Button color="blue" invert class="px-15 round" size="S" :disabled="isFetchingBlueprint" @click="fetchBlueprint">
                      {{ isFetchingBlueprint ? 'Fetching...' : 'Fetch extension' }}
                    </Button>
                    <Button size="S" color="blue" class="px-15 round" :disabled="isLoadingCtPrice" @click="fetchLowestCtPrice">
                      {{ isLoadingCtPrice ? 'Fetching price...' : 'Check CT price' }}
                    </Button>
                    <Button
                      v-if="cardtraderBlueprint && (lowestCtPrice > 0 || cardtraderMarketPrice)"
                      size="S" color="color" invert class="px-15 round"
                      @click="card.real_price = lowestCtPrice > 0 ? lowestCtPrice : Number(cardtraderMarketPrice)"
                    >
                      Set real price = CT ({{ (lowestCtPrice > 0 ? lowestCtPrice : Number(cardtraderMarketPrice)).toFixed(2) }}€)
                    </Button>
                  </p>
                </Box>
              </Boxes>
            </Box>
            <Box size="100">
              <Button color="light-grey" class="round px-15" @click="close">Cancel</Button>
              <Button color="blue" class="round px-15" :disabled="isCreating" @click="save">
                {{ isCreating ? 'Saving...' : 'Save card' }}
              </Button>
            </Box>
          </Boxes>
          <p v-if="error" class="bad-text S m-0">{{ error }}</p>
        </form>
      </Box>
    </Boxes>
  </Modal>
</template>
